import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';

import { getHasBaseSubscription, getIsNonSubscribedWithAccess } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import styles from './UserAvatar.module.scss';
import { CTACheckmarkIcon } from 'components/Icons/CTACheckmarkIcon';

interface UserAvatarProps {
  accountAlert?: boolean | null;
  accountWarning?: boolean;
  isPremiumPlusUser: boolean;
  showNotification?: boolean;
  profilePictureUrl?: string;
  /** Optional progress percentage to render a green progress ring around the avatar */
  progressPercent?: number;
}

const UserAvatar = ({
  accountAlert = false,
  accountWarning = false,
  isPremiumPlusUser,
  showNotification,
  profilePictureUrl,
  progressPercent,
}: UserAvatarProps) => {
  const [imageError, setImageError] = useState(false);
  let isSubscribed = useAppSelector(getHasBaseSubscription);
  const isNonSubscribedWithAccess = useAppSelector(getIsNonSubscribedWithAccess);
  if (isNonSubscribedWithAccess) {
    isSubscribed = false;
  }
  const notification = (!isSubscribed && accountAlert) || accountWarning || showNotification;

  // Clamp progress between 0 and 100 when provided
  const clampedProgress = useMemo(() => {
    if (typeof progressPercent !== 'number' || Number.isNaN(progressPercent)) return undefined;
    if (progressPercent < 0) return 0;
    if (progressPercent > 100) return 100;
    return Math.round(progressPercent);
  }, [progressPercent]);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  useEffect(() => {
    setImageError(false);
  }, [profilePictureUrl]);

  const getRingClassName = () =>
    classNames(styles.ring, {
      [styles['ring--premium-plus']]: isSubscribed && isPremiumPlusUser,
      [styles['ring--premium']]: isSubscribed,
      [styles['ring--alert']]: !isSubscribed && accountAlert,
      [styles['ring--warning']]: accountWarning,
    });

  const getNotificationClassName = () =>
    classNames(styles.notification, {
      [styles['ring--alert']]: !isSubscribed && accountAlert,
      [styles['ring--warning']]: accountWarning,
      [styles['notification--warning']]: showNotification,
      [styles['notification-ring--warning']]: showNotification,
    });

  const renderProgressOverlay = () => {
    if (clampedProgress === undefined) return null;

    const radius = 16;
    const circumference = 2 * Math.PI * radius;
    const progress = clampedProgress / 100;
    const dashOffset = circumference * (1 - progress);

    const circleStyle = {
      strokeDasharray: `${circumference}px`,
      strokeDashoffset: `${dashOffset}px`,
    };

    return (
      <svg
        className={styles.progressSvg}
        viewBox="0 0 34 34"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        {/* Render a gray track below the progress so the remaining portion is visible */}
        <circle
          className={styles.progressTrack}
          cx="17"
          cy="17"
          r={radius}
          strokeWidth="2"
          fill="none"
        />
        <circle
          className={styles.progressCircle}
          cx="17"
          cy="17"
          r={radius}
          strokeWidth="2"
          fill="none"
          style={circleStyle}
        />
      </svg>
    );
  };

  if (profilePictureUrl && !imageError) {
    return (
      <div
        className={classNames(
          styles.profilePictureContainer,
          clampedProgress === undefined && getRingClassName(),
        )}
      >
        {renderProgressOverlay()}
        <Image
          src={profilePictureUrl}
          alt="Profile picture"
          className={styles.profilePicture}
          width={32}
          height={32}
          unoptimized
          onError={handleImageError}
        />
        {notification ? (
          <div className={getNotificationClassName()} data-testid="user-avatar-notification" />
        ) : null}
        {clampedProgress === 100 ? (
          <div className={styles.progressCompleteBadge} aria-hidden>
            <CTACheckmarkIcon strokeFill="#FFFFFF" />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.svgWrapper}>
      {renderProgressOverlay()}
      <svg
        className={styles.notificationSvg}
        width="34px"
        height="34px"
        role="img"
        viewBox="0 0 34 34"
        aria-label="User account icon"
      >
        <g
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          transform="translate(1.000000, 1.000000)"
        >
          {clampedProgress === undefined ? (
            <circle
              className={getRingClassName()}
              strokeWidth="2"
              cx="16"
              cy="16"
              r="16"
              data-testid="user-avatar-ring"
            />
          ) : null}
          <circle stroke="#000" cx="16" cy="13.6" r="6.1" className={styles.userIcon} />
          <path
            className={styles.userIcon}
            d="M26.4493244,27.5833333 C25.9299768,25.0267564 21.4813083,22.9 16,22.9 C10.5186917,22.9 6.07002319,25.0267564 5.55067563,27.5833333"
            stroke="#000"
          />
        </g>
        {notification ? (
          <circle
            className={getNotificationClassName()}
            strokeWidth="2"
            fillRule="nonzero"
            cx="28"
            cy="6"
            r="5"
            data-testid="user-avatar-notification"
          />
        ) : null}
      </svg>
      {clampedProgress === 100 ? (
        <div className={styles.progressCompleteBadge} aria-hidden>
          <CTACheckmarkIcon strokeFill="#FFFFFF" />
        </div>
      ) : null}
    </div>
  );
};

export default UserAvatar;
